import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box } from '@mui/material';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { ActionMenuButton } from 'components/ui/buttons/ActionMenuButton';
import dayjs from 'dayjs';
import { TenantPage } from 'layouts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { equipmentInvoiceService } from 'shared/services';
import EquipmentInvoiceLinesCard from '../components/EquipmentInvoiceLinesCard';
import { EquipmentInvoiceUpdateForm } from '../components/form/EquipmentInvoiceUpdateForm';
import { useEquipmentInvoice } from '../hooks/useEquipmentInvoice';

const INVOICE_ATTACHMENT_FILE_KEYS = [
  'invoice_file',
  'ddt_file',
  'invoice_revision_file',
  'extra_1_file',
  'extra_2_file',
  'extra_3_file'
];

export function EquipmentInvoiceDetail() {
  const { t } = useTranslation();
  const prefix = 'pages.equipment-invoices.detail';
  const { invoiceId } = useParams();

  const {
    invoice,
    isCreditMemoGenerating,
    isLinesDialogOpen,
    setIsLinesDialogOpen,
    updateInvoice,
    downloadInvoiceAgreement,
    sendCreditMemoEmail,
    sendInvoiceAgreementEmail,
    generateAndDownloadCreditMemo
  } = useEquipmentInvoice(invoiceId);

  const handleCloseLinesDialog = useCallback(() => {
    setIsLinesDialogOpen(false);
  }, []);

  const handleOpenLinesDialog = useCallback(() => {
    setIsLinesDialogOpen(true);
  }, []);

  const tabConfig: TabItem[] =
    invoiceId && invoice
      ? [
          {
            label: t(`global.base-info`),
            component: (
              <EquipmentInvoiceUpdateForm invoice={invoice} updateInvoice={updateInvoice} />
            )
          },
          {
            label: t(`${prefix}.invoice-lines`),
            disablePadding: true,
            component: (
              <EquipmentInvoiceLinesCard
                invoiceId={Number(invoiceId)}
                contractId={Number(invoice?.contract)}
                isOpen={isLinesDialogOpen}
                handleOpenDialog={handleOpenLinesDialog}
                handleCloseDialog={handleCloseLinesDialog}
              />
            )
          }
        ]
      : [];

  const incoherentAmounts = useMemo(() => {
    return invoice && invoice.amount != null && +invoice.amount !== +invoice.expected_amount;
  }, [invoice?.amount, invoice?.expected_amount]);

  return (
    <TenantPage
      title={t(`${prefix}.title`) + ' ' + invoice?.ref}
      subtitle={t(`${prefix}.subtitle`)}
      entityCode={invoice?.ref}
      menuRight={
        <Box className="flex flex-wrap items-center gap-3">
          <ActionMenuButton
            label={t(`${prefix}.credit-note`)}
            disabled={isCreditMemoGenerating}
            items={[
              invoice?.credit_memo_file && {
                label: isCreditMemoGenerating ? t('Generazione in corso..') : 'Scarica',
                loading: isCreditMemoGenerating,
                icon: <DownloadIcon />,
                onClick: generateAndDownloadCreditMemo
              },
              invoice?.credit_memo_file && {
                label: 'Invia',
                icon: <SendIcon />,
                onClick: sendCreditMemoEmail
              }
            ]}
          />
          <ActionMenuButton
            label={t(`${prefix}.invoice-agreement`)}
            items={[
              {
                label: 'Scarica',
                icon: <DownloadIcon />,
                onClick: downloadInvoiceAgreement
              },
              {
                label: 'Invia',
                icon: <SendIcon />,
                onClick: sendInvoiceAgreementEmail
              }
            ]}
          />
        </Box>
      }>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'equipment_invoice',
          entityId: invoice?.id,
          dictParams: { equipment_invoice: invoiceId },
          customFolderName: `menu.equipment-invoices/${invoice?.ref ?? invoice?.id}`,
          folderDescription: `Contratto: ${invoice?.contract}; Data: ${
            invoice?.date ? dayjs(invoice?.date).format('DD/MM/YYYY') : ''
          }; `
        }}
        attachmentsConfig={{
          fields: INVOICE_ATTACHMENT_FILE_KEYS,
          entity: invoice,
          service: equipmentInvoiceService,
          translationPrefix: 'entities.ddt'
        }}
      />

      {incoherentAmounts && (
        <div className="w-full my-4">
          <Alert severity="warning" className="w-full">
            {t(`${prefix}.incoerent-amounts`)}
          </Alert>
        </div>
      )}
    </TenantPage>
  );
}

export default EquipmentInvoiceDetail;
